'use client';

import type { QueryClient } from '@tanstack/react-query';
import { buildQueryString, fetchCachedQuery, fetchJson } from '@/lib/api/client';
import {
  contactDetailRequest,
  contactListRequest,
  contactLogsRequest,
  contactQuickSearchRequest,
  contactsQueryKeys,
} from '@/lib/api/keys';
import type {
  ContactBulkAction,
  CampaignVisibilityListPayload,
  CampaignVisibilityPayload,
  Contact,
  ContactImportJobDetail,
  ContactImportJobSummary,
  ContactImportRollbackResult,
  ContactLifecycleFilter,
  ContactFieldDefinition,
  ContactTag,
  ContactLogQuery,
  ContactLogsPayload,
  CreateContactFieldPayload,
  CreateContactPayload,
  CsvPreviewPayload,
  CsvPreviewRequestPayload,
  CsvRunPayload,
  CsvRunRequestPayload,
  SaveCampaignFieldVisibilityPayload,
  UpsertContactTagPayload,
  UpdateContactPayload,
} from '@/types/contacts';


// Base64-encode the streamed-import options for the X-Import-Meta header. The
// request body is the raw CSV, so the options travel out of band; base64 of the
// UTF-8 JSON keeps non-ASCII header names (e.g. Norwegian) header-safe.
function encodeImportMeta(meta: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(meta));
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

// Cache key (and request value) for the caller-ID phone lookup: digits and a
// leading + only, so every formatting of the same number collapses onto one
// cache entry. Mirrors what use-contact-by-phone.ts feeds the lookup.
export function contactLookupPhoneKey(phoneLike: string): string {
  return phoneLike.replace(/[^\d+]/g, '').trim();
}

// Session cache for the caller-ID phone lookup: a contact's identity for
// caller-ID purposes doesn't change mid-shift, so repeat calls to/from the
// same number stop re-fetching. Same idea as sip.ts getDidOptions' 5-minute
// react-query cache, but this lookup also runs from the softphone runtime,
// which mounts OUTSIDE the QueryClientProvider — so the cache lives here.
// Caching the promise dedups concurrent in-flight lookups too.
const PHONE_LOOKUP_TTL_MS = 10 * 60_000;
const phoneLookupCache = new Map<string, { at: number; promise: Promise<Contact | null> }>();

export type ContactListPage = {
  ok: true;
  contacts: Contact[];
  total: number;
  limit: number;
  offset: number;
};

export interface ContactQuickSearchResult {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  companyName: string | null;
}


export interface ContactEmailEndpoint {
  id: string;
  email: string;
  label: string | null;
  isPrimary: boolean;
}

interface ContactPhoneEndpoint {
  id: string;
  phone: string;
  label: string | null;
  isPrimary: boolean;
  lineType: string | null;
}

export interface ContactEndpointSets {
  emails: ContactEmailEndpoint[];
  phones: ContactPhoneEndpoint[];
}

export const contactsApi = {

  /** All emails/phones on record for a contact (primary first). */
  endpoints(contactId: string): Promise<ContactEndpointSets> {
    return fetchJson<{ ok: true } & ContactEndpointSets>(`/api/contacts/${contactId}/endpoints`).then(
      ({ emails, phones }) => ({ emails, phones })
    );
  },

  addContactEndpoint(
    contactId: string,
    kind: 'emails' | 'phones',
    value: string,
    label: string | null
  ): Promise<ContactEndpointSets> {
    return fetchJson<{ ok: true } & ContactEndpointSets>(`/api/contacts/${contactId}/${kind}`, {
      method: 'POST',
      body: JSON.stringify(kind === 'emails' ? { email: value, label } : { phone: value, label }),
    }).then(({ emails, phones }) => ({ emails, phones }));
  },

  removeContactEndpoint(contactId: string, kind: 'emails' | 'phones', endpointId: string): Promise<ContactEndpointSets> {
    return fetchJson<{ ok: true } & ContactEndpointSets>(`/api/contacts/${contactId}/${kind}/${endpointId}`, {
      method: 'DELETE',
    }).then(({ emails, phones }) => ({ emails, phones }));
  },

  setPrimaryContactEndpoint(contactId: string, kind: 'emails' | 'phones', endpointId: string): Promise<ContactEndpointSets> {
    return fetchJson<{ ok: true } & ContactEndpointSets>(`/api/contacts/${contactId}/${kind}/${endpointId}/primary`, {
      method: 'POST',
    }).then(({ emails, phones }) => ({ emails, phones }));
  },
  // Lightweight, trigram-indexed search for the Cmd+K command palette. Returns
  // minimal fields and is cached for 30s so retyping a recent query is instant.
  quickSearchContacts(
    queryClient: QueryClient,
    search: string,
    limit = 8
  ): Promise<ContactQuickSearchResult[]> {
    const request = contactQuickSearchRequest(search, limit);
    return fetchCachedQuery<{ ok: true; contacts: ContactQuickSearchResult[] }>(
      queryClient,
      request.key,
      request.path,
      request.staleTime
    ).then((payload) => payload.contacts);
  },

  listContacts(
    queryClient: QueryClient,
    options: {
      search?: string;
      limit?: number;
      offset?: number;
      campaignId?: string | null;
      lifecycleStatus?: ContactLifecycleFilter | null;
      isActive?: boolean | null;
      tagId?: string | null;
      ownerUserId?: string | null;
      activityType?: string | null;
      duplicateCandidates?: boolean | null;
    } = {}
  ): Promise<Contact[]> {
    return this.listContactsPage(queryClient, options).then((payload) => payload.contacts);
  },

  listContactsPage(
    queryClient: QueryClient,
    options: {
      search?: string;
      limit?: number;
      offset?: number;
      campaignId?: string | null;
      lifecycleStatus?: ContactLifecycleFilter | null;
      isActive?: boolean | null;
      tagId?: string | null;
      ownerUserId?: string | null;
      activityType?: string | null;
      duplicateCandidates?: boolean | null;
    } = {}
  ): Promise<ContactListPage> {
    const request = contactListRequest({
      search: options.search?.trim() || '',
      limit: options.limit ?? 300,
      offset: options.offset ?? 0,
      campaignId: options.campaignId || null,
      lifecycleStatus: options.lifecycleStatus || null,
      isActive: options.isActive ?? null,
      tagId: options.tagId || null,
      ownerUserId: options.ownerUserId || null,
      activityType: options.activityType || null,
      duplicateCandidates: options.duplicateCandidates ?? false,
    });
    return fetchCachedQuery<ContactListPage>(queryClient, request.key, request.path, request.staleTime);
  },

  getContact(queryClient: QueryClient, contactId: string): Promise<Contact> {
    const request = contactDetailRequest(contactId);
    return fetchCachedQuery<{ ok: true; contact: Contact }>(queryClient, request.key, request.path, request.staleTime)
      .then((payload) => payload.contact);
  },

  async findContactByPhone(phone: string): Promise<Contact | null> {
    const normalized = contactLookupPhoneKey(phone);
    if (!normalized) {
      return null;
    }
    const now = Date.now();
    const cached = phoneLookupCache.get(normalized);
    if (cached && now - cached.at < PHONE_LOOKUP_TTL_MS) {
      return cached.promise;
    }
    // Drop entries past their TTL so a long shift can't grow the map unboundedly.
    for (const [key, entry] of phoneLookupCache) {
      if (now - entry.at >= PHONE_LOOKUP_TTL_MS) {
        phoneLookupCache.delete(key);
      }
    }
    const promise = fetchJson<{ ok: true; contact: Contact | null }>(
      `/api/contacts/lookup-by-phone?phone=${encodeURIComponent(normalized)}`
    ).then((payload) => payload.contact);
    phoneLookupCache.set(normalized, { at: now, promise });
    // A failed lookup must not poison the cache for 10 minutes.
    promise.catch(() => {
      const entry = phoneLookupCache.get(normalized);
      if (entry && entry.promise === promise) {
        phoneLookupCache.delete(normalized);
      }
    });
    return promise;
  },

  getContactLogs(
    queryClient: QueryClient,
    contactId: string,
    options: ContactLogQuery = {}
  ): Promise<ContactLogsPayload['logs']> {
    const request = contactLogsRequest(contactId, options);
    return fetchCachedQuery<ContactLogsPayload>(queryClient, request.key, request.path, request.staleTime)
      .then((payload) => payload.logs);
  },

  createContact(payload: CreateContactPayload): Promise<Contact> {
    return fetchJson<{ ok: true; contact: Contact }>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((result) => result.contact);
  },

  updateContact(contactId: string, payload: UpdateContactPayload): Promise<Contact> {
    return fetchJson<{ ok: true; contact: Contact }>(`/api/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }).then((result) => result.contact);
  },

  refreshDemoWebsiteLink(contactId: string): Promise<Contact> {
    return fetchJson<{ ok: true; contact: Contact }>(`/api/contacts/${contactId}/demo-website-link/refresh`, {
      method: 'POST',
    }).then((result) => result.contact);
  },

  bulkUpdateContacts(payload: {
    action: ContactBulkAction;
    contactIds: string[];
    campaignId?: string;
    ownerUserId?: string | null;
  }): Promise<{ ok: true; changed: number; skipped?: number; action: ContactBulkAction }> {
    return fetchJson<{ ok: true; changed: number; skipped?: number; action: ContactBulkAction }>(
      '/api/admin/contacts/bulk',
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
  },

  previewImport(payload: CsvPreviewRequestPayload): Promise<CsvPreviewPayload> {
    return fetchJson<CsvPreviewPayload>('/api/admin/contacts/import/preview', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  runImport(payload: CsvRunRequestPayload): Promise<CsvRunPayload> {
    return fetchJson<CsvRunPayload>('/api/admin/contacts/import/run', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Large-file path: stream the raw File as the request body so the browser
  // never JSON-encodes the whole file into memory, and carry the import options
  // in a small base64 header. The backend parses the body row-by-row.
  runImportStream(file: File, options: Omit<CsvRunRequestPayload, 'csvText'>): Promise<CsvRunPayload> {
    return fetchJson<CsvRunPayload>('/api/admin/contacts/import/run', {
      method: 'POST',
      headers: {
        'Content-Type': 'text/csv',
        'X-Import-Meta': encodeImportMeta(options),
      },
      body: file,
    });
  },

  listImportJobs(
    queryClient: QueryClient,
    options: { limit?: number; offset?: number } = {}
  ): Promise<ContactImportJobSummary[]> {
    const limit = options.limit ?? 30;
    const offset = options.offset ?? 0;
    return fetchCachedQuery<{ ok: true; jobs: ContactImportJobSummary[] }>(
      queryClient,
      contactsQueryKeys.importJobs(limit, offset),
      `/api/admin/contacts/import/jobs${buildQueryString({ limit, offset })}`,
      1_500
    ).then((payload) => payload.jobs);
  },

  getImportJob(queryClient: QueryClient, jobId: string): Promise<ContactImportJobDetail> {
    return fetchCachedQuery<{ ok: true; job: ContactImportJobDetail }>(
      queryClient,
      contactsQueryKeys.importJob(jobId),
      `/api/admin/contacts/import/jobs/${jobId}`,
      1_500
    ).then((payload) => payload.job);
  },

  rollbackImportJob(jobId: string): Promise<ContactImportRollbackResult> {
    return fetchJson<{ ok: true; rollback: ContactImportRollbackResult }>(
      `/api/admin/contacts/import/jobs/${jobId}/rollback`,
      { method: 'POST' }
    ).then((payload) => payload.rollback);
  },

  listContactFields(queryClient: QueryClient, includeInactive: boolean): Promise<ContactFieldDefinition[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    const path = includeInactive ? '/api/admin/contact-fields' : '/api/contact-fields';
    return fetchCachedQuery<{ ok: true; fields: ContactFieldDefinition[] }>(
      queryClient,
      contactsQueryKeys.fields(includeInactive),
      `${path}${query}`,
      includeInactive ? 2_000 : 10_000
    ).then((payload) => payload.fields);
  },

  createContactField(payload: CreateContactFieldPayload): Promise<void> {
    return fetchJson('/api/admin/contact-fields', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then(() => undefined);
  },

  updateContactField(fieldId: string, payload: Record<string, unknown>): Promise<void> {
    return fetchJson(`/api/admin/contact-fields/${fieldId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }).then(() => undefined);
  },

  deactivateContactField(fieldId: string): Promise<void> {
    return fetchJson(`/api/admin/contact-fields/${fieldId}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },

  listContactTags(queryClient: QueryClient, includeInactive = false): Promise<ContactTag[]> {
    const query = includeInactive ? '?includeInactive=true' : '';
    const path = includeInactive ? '/api/admin/contact-tags' : '/api/contact-tags';
    return fetchCachedQuery<{ ok: true; tags: ContactTag[] }>(
      queryClient,
      contactsQueryKeys.tags(includeInactive),
      `${path}${query}`,
      includeInactive ? 2_000 : 10_000
    ).then((payload) => payload.tags);
  },

  createContactTag(payload: UpsertContactTagPayload): Promise<ContactTag> {
    return fetchJson<{ ok: true; tag: ContactTag }>('/api/admin/contact-tags', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((payload) => payload.tag);
  },

  updateContactTag(tagId: string, payload: UpsertContactTagPayload): Promise<ContactTag> {
    return fetchJson<{ ok: true; tag: ContactTag }>(`/api/admin/contact-tags/${tagId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }).then((payload) => payload.tag);
  },

  getCampaignFieldVisibility(queryClient: QueryClient, campaignKey: string): Promise<CampaignVisibilityListPayload> {
    return fetchCachedQuery<CampaignVisibilityListPayload>(
      queryClient,
      contactsQueryKeys.campaignFieldVisibility(campaignKey),
      `/api/campaign-field-visibility?campaignKey=${encodeURIComponent(campaignKey)}`,
      2_000
    );
  },

  getAdminCampaignFieldVisibility(queryClient: QueryClient, campaignKey: string): Promise<CampaignVisibilityPayload> {
    return fetchCachedQuery<CampaignVisibilityPayload>(
      queryClient,
      contactsQueryKeys.adminCampaignFieldVisibility(campaignKey),
      `/api/admin/campaign-field-visibility?campaignKey=${encodeURIComponent(campaignKey)}`,
      2_000
    );
  },

  saveCampaignFieldVisibility(
    campaignKey: string,
    payload: SaveCampaignFieldVisibilityPayload
  ): Promise<void> {
    return fetchJson(`/api/admin/campaign-field-visibility?campaignKey=${encodeURIComponent(campaignKey)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then(() => undefined);
  },
};
